// API工具函数 - 交易所充提币状态看板

interface FilterOptions {
  tokens: Array<{ value: string; label: string }>;
  chains: Array<{ value: string; label: string }>;
  exchanges: Array<{ value: string; label: string }>;
}

interface StatusRecord {
  key?: string; // 可选的key字段，用于React列表渲染
  exchange: string;
  token: string;
  chain: string;
  can_deposit: boolean;
  can_withdraw: boolean;
  deposit_fee: string;
  withdrawal_fee: string;
  min_deposit: string;
  min_withdrawal: string;
  deposit_confirmations: number;
  withdrawal_confirmations: number;
  status_notes?: string | null;
  last_updated_at?: string;
}

interface StatusResponse {
  data: StatusRecord[];
  last_updated_timestamp: string;
  total_records?: number;
}

interface FilterParams {
  token: string;
  chain?: string;
  exchange?: string;
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.519312.xyz/api/v1';

/**
 * 获取筛选选项数据
 */
export const fetchFilterOptions = async (): Promise<FilterOptions> => {
  try {
    const response = await fetch(`${API_BASE_URL}/exchange/options`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    // 常见代币列表，按优先级排序
    const priorityTokens = ['USDT', 'USDC', 'BTC', 'ETH', 'SOL'];
    
    // 常见链列表，按优先级排序
    const priorityChains = ['ETH', 'BSC', 'SOL', 'Polygon', 'TRX', 'AVAX', 'Base', 'ARB', 'OP', 'SUI', 'TON'];
    
    // 对代币列表进行排序：常见代币置顶，其他按字母排序
    const sortedTokens = [...data.tokens].sort((a, b) => {
      const aIndex = priorityTokens.indexOf(a.value);
      const bIndex = priorityTokens.indexOf(b.value);
      
      // 如果都是常见代币，按优先级排序
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }
      
      // 如果只有a是常见代币，a排在前面
      if (aIndex !== -1) {
        return -1;
      }
      
      // 如果只有b是常见代币，b排在前面
      if (bIndex !== -1) {
        return 1;
      }
      
      // 都不是常见代币，按字母排序
      return a.label.localeCompare(b.label);
    });
    
    // 对链列表进行排序：ALL选项置顶，常见链其次，其他按字母排序
    const sortedChains = [...data.chains].sort((a, b) => {
      // ALL选项始终排在第一位
      if (a.value === 'ALL') return -1;
      if (b.value === 'ALL') return 1;
      
      const aIndex = priorityChains.indexOf(a.value);
      const bIndex = priorityChains.indexOf(b.value);
      
      // 如果都是常见链，按优先级排序
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }
      
      // 如果只有a是常见链，a排在前面
      if (aIndex !== -1) {
        return -1;
      }
      
      // 如果只有b是常见链，b排在前面
      if (bIndex !== -1) {
        return 1;
      }
      
      // 都不是常见链，按字母排序
      return a.label.localeCompare(b.label);
    });
    
    return {
      ...data,
      tokens: sortedTokens,
      chains: sortedChains
    };
  } catch (error) {
    console.error('Failed to fetch filter options:', error);
    throw error;
  }
};

/**
 * 查询充提币状态
 */
export const fetchTransferStatus = async (params: FilterParams): Promise<StatusResponse> => {
  try {
    const searchParams = new URLSearchParams();
    searchParams.append('token', params.token);
    
    if (params.chain && params.chain !== 'ALL') {
      searchParams.append('chain', params.chain);
    }
    
    if (params.exchange && params.exchange !== 'ALL') {
      searchParams.append('exchange', params.exchange);
    }

    const response = await fetch(`${API_BASE_URL}/exchange/status?${searchParams.toString()}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Failed to fetch transfer status:', error);
    throw error;
  }
};

/**
 * 格式化日期时间
 */
export const formatDateTime = (timestamp: string): string => {
  try {
    const date = new Date(timestamp);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  } catch (error) {
    console.error('Failed to format date:', error);
    return '无效时间';
  }
};

/**
 * 根据状态分类数据
 */
export const categorizeStatusData = (data: StatusRecord[]) => {
  return {
    withdrawalPaused: data.filter(record => !record.can_withdraw),
    depositPaused: data.filter(record => !record.can_deposit),
    availableWithdrawal: data.filter(record => record.can_withdraw),
    availableDeposit: data.filter(record => record.can_deposit)
  };
};

/**
 * 获取系统统计信息
 */
export const fetchSystemStatistics = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/exchange/statistics`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Failed to fetch system statistics:', error);
    throw error;
  }
};

/**
 * 健康检查
 */
export const healthCheck = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/exchange/health`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Health check failed:', error);
    throw error;
  }
};

/**
 * 手动刷新数据
 */
export const refreshData = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/exchange/refresh`, {
      method: 'POST'
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Failed to refresh data:', error);
    throw error;
  }
};

export type { FilterOptions, StatusRecord, StatusResponse, FilterParams }; 